import { Device, Document, DocumentAnswer, DocumentVersion } from "@models";
import { AnswerItem, Template, TemplateQuestion } from "@types";
import {
  getDocumentStepsFromConfig,
  getFilteredElements,
} from "src/utils/documents";

export const isAnswerItemsComplete = (
  answerItems: AnswerItem[],
  step: TemplateQuestion
): boolean => {
  return answerItems.every((item) => {
    // Archived items are considered complete
    if (item.archived) {
      return true;
    }

    if (step.element.type !== "answerItemsElement") {
      throw new Error("Step is not an answerItemsElement");
    }

    // Items without a creator or in editing mode are drafts and are not saved yet.
    if (item.createdBy === "" || item.createdBy === "editing") {
      return false;
    }

    return step.element.options.fields.every((field) => {
      const fieldValue = item.fields[field.fieldKey];

      if (!field.required) {
        return true;
      }

      if (fieldValue === undefined) {
        return false;
      }

      if (typeof fieldValue === "string" && fieldValue.trim() === "") {
        return false;
      }

      return true;
    });
  });
};

export const isStepComplete = (
  step: TemplateQuestion,
  answer: Partial<DocumentAnswer>
): boolean => {
  if (!step.required) {
    return true;
  }

  if (step.element.type === "fileUpload") {
    return !!answer?.answerFileId;
  }

  if (step.element.type === "answerItemsElement") {
    const answerItems = answer.answerItems;
    if (!Array.isArray(answerItems)) {
      return false;
    }

    return isAnswerItemsComplete(answerItems, step);
  }

  // For all other element types, check for non-empty answer or default value
  const hasNonEmptyAnswer = !!answer?.answer && answer.answer.trim() !== "";
  const hasDefaultValue = !!step.element.options?.default;

  return hasNonEmptyAnswer || hasDefaultValue;
};

export const allStepsCompleted = (
  documentVersion: DocumentVersion,
  device: Device,
  documents: Document[],
  templateConfig: Template
) => {
  const steps = getDocumentStepsFromConfig(templateConfig);

  const filteredSteps = getFilteredElements(steps, device, documents);

  return filteredSteps.every((step) => {
    const stepAnswer = documentVersion.answers.find(
      (a) => a.element === step.id
    );

    return isStepComplete(step, stepAnswer || {});
  });
};
